import {
  Accordion,
  columnTypesConst,
  CustomActionItem,
  CustomButton,
  SvgAddComment,
  SvgGreenTick,
  SvgQueueEdit,
  SvgRevert,
  SvgTrash,
  TableColumnType,
  Tooltip,
} from '@ui-kit'
import {
  DataType,
  DataTypeStore,
  EVENT_CONSTANTS,
  WORKFLOW_UI_IDENTIFIER,
} from '@app-store'
import { DATA_CHECK, NAMESPACES, TASK_STATUS } from '../../../utils/constants'
import { useEffect, useMemo, useState } from 'react'
import { TAB_INDEX } from '../../../constants/form.constants'
import PersistTable from '../../../common/PersistTable'
import { useAppSelector } from '../../../utils/agent.store'
import AddCommentModal from './AddCommentModal'
import { formatLocalDateWithZone } from '../../../utils/date.utils'
import { DataCheckPopupType } from '../../../types/dataCorrection.types'
import AccordionFormWithTable from '../../../common/AccordionFormWithTable'
import { AddDataFields } from './AddDataCorrections'

export const DataCheckPopup = ({
  pendingCorrectionData,
  setPendingCorrectionData,
  resolvedCorrectionData,
  setResolvedCorrectionData,
  handleSave,
  handleCancel,
  overallDataCheckDetails,
  isViewMode,
}: DataCheckPopupType) => {
  const currentUser = useAppSelector((state) => state?.authUser?.user)
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false)
  const [commentValue, setCommentValue] = useState('')
  const [commentRowIndex, setCommentRowIndex] = useState<number | null>(null)
  const [commentSourceTable, setCommentSourceTable] = useState<
    'pending' | 'resolved' | null
  >(null)
  const [commentRowData, setCommentRowData] = useState<DataType | null>(null)
  const [selectedRow, setSelectedRow] = useState<DataType | null>(null)
  const [pendingTableHeight, setPendingTableHeight] = useState<number>(10)
  const [resolvedTableHeight, setResolvedTableHeight] = useState<number>(10)

  const updatedTaskList = useAppSelector(
    (state) => state?.workflowData?.taskList
  )
  const columnList: TableColumnType<DataTypeStore>[] = [
    {
      header: DATA_CHECK.FIELD_NAME,
      accessor: DATA_CHECK.FIELD_NAME_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.ISSUE_DESCRIPTION,
      accessor: DATA_CHECK.ISSUE_DESCRIPTION_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.ERROR_TYPE,
      accessor: DATA_CHECK.ERROR_TYPE_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.STATUS,
      accessor: DATA_CHECK.FIELD_STATUS_LOWERCASE,
      isSortable: false,
    },

    {
      header: DATA_CHECK.ADDED_ON,
      accessor: DATA_CHECK.CREATED_AT_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.ADDED_BY,
      accessor: DATA_CHECK.CREATED_BY_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.COMMENTS,
      accessor: DATA_CHECK.REMARK_LOWERCASE,
      isSortable: false,
    },
  ]
  const resolvedList: TableColumnType<DataTypeStore>[] = [
    ...columnList.slice(0, -2),

    {
      header: DATA_CHECK.RESOLVED_ON,
      accessor: DATA_CHECK.RESOLVED_ON_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.RESOLVED_BY,
      accessor: DATA_CHECK.RESOLVED_BY_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.COMMENTS,
      accessor: DATA_CHECK.REMARK_LOWERCASE,
      isSortable: false,
    },
  ]

  const getResolvedBy = () =>
    (currentUser?.first_name ?? '' + currentUser?.last_name) as string
  const getResolvedOnLabel = () =>
    formatLocalDateWithZone(new Date().toISOString())
  const getResolvedOn = () => new Date().toISOString()
  const movePendingRowToResolved = (row: DataType | null, rowIndex: number) => {
    if (!row) return

    const resolvedRow = {
      ...row,
      field_status: DATA_CHECK.RESOLVED,
      status: true,
      resolved_by: getResolvedBy(),
      resolved_on: getResolvedOn(),
      resolved_on_label: getResolvedOnLabel(),
      disabled: false,
    }
    const updatedPending = pendingCorrectionData.filter(
      (_, i) => i !== rowIndex
    )
    setPendingCorrectionData(updatedPending)
    setResolvedCorrectionData((prev) => [resolvedRow, ...prev])
  }

  const moveResolvedRowToPending = (row: DataType | null, rowIndex: number) => {
    if (!row) return

    const pendingRow = {
      ...row,
      status: false,
      field_status: DATA_CHECK.PENDING,
      resolved_by: '',
      resolved_on: '',
      resolved_on_label: '',
      disabled: false,
    }
    const updatedResolved = resolvedCorrectionData.filter(
      (_, i) => i !== rowIndex
    )
    setResolvedCorrectionData(updatedResolved)
    setPendingCorrectionData((prev) => [pendingRow, ...prev])
  }
  const openAddCommentModal = (
    row: DataType | null,
    rowIndex: number,
    sourceTable: 'pending' | 'resolved'
  ) => {
    if (!row) return

    setCommentSourceTable(sourceTable)
    setCommentRowIndex(rowIndex)
    setCommentRowData(row)
    setCommentValue((row.remark || '') as string)
    setIsAddCommentModalOpen(true)
  }

  const closeAddCommentModal = () => {
    setIsAddCommentModalOpen(false)
    setCommentSourceTable(null)
    setCommentRowIndex(null)
    setCommentRowData(null)
    setCommentValue('')
  }

  const handleSaveComment = () => {
    if (commentRowIndex === null || !commentSourceTable) return

    if (commentSourceTable === 'pending') {
      const updatedPending = pendingCorrectionData.map((item, idx) =>
        idx === commentRowIndex
          ? { ...item, remark: commentValue.trim() }
          : item
      )
      setPendingCorrectionData(updatedPending)
    } else {
      setResolvedCorrectionData((prev) =>
        prev.map((item, idx) =>
          idx === commentRowIndex
            ? { ...item, remark: commentValue.trim() }
            : item
        )
      )
    }

    closeAddCommentModal()
  }

  //TODO: Check and update the conditional visibility of these actions
  const actions = [
    {
      label: DATA_CHECK.RESOLVE,
      key: DATA_CHECK.RESOLVE?.toLowerCase(),
      accessor: DATA_CHECK.RESOLVE?.toLowerCase(),
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        return (
          <CustomActionItem
            row={row}
            tooltip={DATA_CHECK.MARK_AS_RESOLVED}
            dataQaId="resolve-data-correction"
            onClick={() => movePendingRowToResolved(row, rowIndex)}
            icon={<SvgGreenTick />}
          />
        )
      },
    },
    {
      label: NAMESPACES.EDIT_BUTTON,
      key: NAMESPACES.EDIT_KEY,
      accessor: NAMESPACES.EDIT_KEY,
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        // if (row?.manually_inserted) {
        return (
          <Tooltip content={NAMESPACES.EDIT_BUTTON} position="top">
            <button
              type="button"
              onClick={() => {
                setSelectedRow({ ...row, rowIndex: rowIndex })
                setPendingCorrectionData((prev) =>
                  prev.map((item, i) =>
                    i === rowIndex
                      ? { ...item, disabled: true }
                      : { ...item, disabled: false }
                  )
                )
                window.dispatchEvent(
                  new Event(EVENT_CONSTANTS.DISABLE_FOCUS_TRAP)
                )
              }}
              className={`${row?.disabled ? 'pointer-events-none' : ''}`}
              data-qa-id="edit-correction-info"
            >
              <SvgQueueEdit />
            </button>
          </Tooltip>
        )
        // } else return null
      },
    },
    {
      label: DATA_CHECK.ADD_COMMENT,
      key: DATA_CHECK.ADD_COMMENT.toLowerCase(),
      accessor: DATA_CHECK.ADD_COMMENT.toLowerCase(),
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        return (
          <CustomActionItem
            row={row}
            tooltip={DATA_CHECK.ADD_COMMENT}
            dataQaId="add-comment-pending"
            onClick={() => openAddCommentModal(row, rowIndex, 'pending')}
            icon={<SvgAddComment />}
          />
        )
      },
    },
    {
      label: NAMESPACES.DELETE,
      key: NAMESPACES.DELETE_KEY,
      accessor: NAMESPACES.DELETE_KEY,
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        return (
          <Tooltip content={NAMESPACES.DELETE} position="top">
            <button
              type="button"
              onClick={() => {
                const updatedCorrectionFields = pendingCorrectionData.filter(
                  (_, i) => i !== rowIndex
                )
                setPendingCorrectionData(updatedCorrectionFields)
              }}
              className={`${row?.disabled ? 'pointer-events-none' : ''}`}
              data-qa-id="delete-correction-info"
            >
              <SvgTrash height="1rem" width="1rem" />
            </button>
          </Tooltip>
        )
      },
    },
  ]
  const resolvedActions = [
    {
      label: DATA_CHECK.MARK_AS_PENDING,
      key: DATA_CHECK.MARK_AS_PENDING?.toLowerCase(),
      accessor: DATA_CHECK.MARK_AS_PENDING?.toLowerCase(),
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        return (
          <CustomActionItem
            row={row}
            tooltip={DATA_CHECK.MARK_AS_PENDING}
            dataQaId="move-to-pending-data-correction"
            onClick={() => moveResolvedRowToPending(row, rowIndex)}
            icon={<SvgRevert />}
            className="mt-1"
          />
        )
      },
    },
    {
      label: DATA_CHECK.ADD_COMMENT,
      key: `${DATA_CHECK.ADD_COMMENT.toLowerCase()}-resolved`,
      accessor: `${DATA_CHECK.ADD_COMMENT.toLowerCase()}-resolved`,
      showInPopUp: false,
      type: columnTypesConst.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        return (
          <CustomActionItem
            row={row}
            tooltip={DATA_CHECK.ADD_COMMENT}
            dataQaId="add-comment-resolved"
            onClick={() => openAddCommentModal(row, rowIndex, 'resolved')}
            icon={<SvgAddComment />}
            className="mt-1"
          />
        )
      },
    },
  ]
  const onSubmit = (data: DataType | null) => {
    if (data && selectedRow) {
      const updatedCorrectionData = pendingCorrectionData.map((item, i) => {
        let updatedRowData: DataType | null = null
        if (i !== selectedRow?.rowIndex) return item
        const mergedData = { ...item, ...data }
        updatedRowData = {
          ...mergedData,
          created_at: getResolvedOn(),
          created_by: getResolvedBy(),
          created_at_label: getResolvedOnLabel(),
          status: false,
          field_status: DATA_CHECK.PENDING,
          disabled: false,
          _state: selectedRow?.id ? 'CHANGED' : 'ADDED',
        }

        return updatedRowData
      })
      setPendingCorrectionData(updatedCorrectionData)
    } else if (data) {
      const newItem = {
        ...data,
        created_at: getResolvedOn(),
        created_by: getResolvedBy(),
        created_at_label: getResolvedOnLabel(),
        status: false,
        field_status: DATA_CHECK.PENDING,
        _state: 'ADDED',
      }

      setPendingCorrectionData((prev) => [newItem, ...prev])
    } else {
      setPendingCorrectionData((prev) =>
        prev.map((item) => ({
          ...item,
          disabled: false,
        }))
      )
    }

    setSelectedRow(null)
  }

  const isDataCheckInProcess = useMemo(() => {
    return updatedTaskList?.find(
      (task) =>
        task?.identifier?.ui_identifier ===
          WORKFLOW_UI_IDENTIFIER.DATA_CHECK_LAYOUT &&
        task?.state !== TASK_STATUS.COMPLETED
    )
  }, [updatedTaskList])

  useEffect(() => {
    setPendingTableHeight(pendingCorrectionData?.length >= 3 ? 15 : 10)
  }, [pendingCorrectionData])

  useEffect(() => {
    setResolvedTableHeight(resolvedCorrectionData?.length >= 3 ? 15 : 10)
  }, [resolvedCorrectionData])

  useEffect(() => {
    setResolvedCorrectionData(resolvedCorrectionData || [])
  }, [resolvedCorrectionData])

  return (
    <div className="flex flex-col gap-1 pb-[0.625rem]">
      <span className="flex justify-between items-center mb-1">
        <span className="text-xl font-medium">
          {DATA_CHECK.DATA_CHECK_SUMMARY}
        </span>
        <span className="flex gap-3">
          <CustomButton type="button" onClick={handleCancel}>
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>
          <>
            {/* <CustomButton isFilled type="button" onClick={handleReValidate}>
                {DATA_CHECK.RECHECK_ELIGIBILITY}
              </CustomButton> */}
            {!isViewMode ? (
              <CustomButton
                isFilled
                type="button"
                onClick={() => handleSave(false)}
              >
                {NAMESPACES.SAVE_BUTTON}
              </CustomButton>
            ) : null}
            {isDataCheckInProcess ? (
              <Tooltip
                content={
                  pendingCorrectionData?.find(
                    (item) => item.error_type_label === 'Major'
                  )
                    ? DATA_CHECK.DATA_CHECK_FINALIZE_TOOLTIP
                    : overallDataCheckDetails?.every(
                        (item) => item.final_check_done
                      )
                    ? DATA_CHECK.DISABLE_FINALIZE_BUTTON_TOOLTIP
                    : ''
                }
                position="top"
              >
                <CustomButton
                  isFilled
                  type="button"
                  disabled={
                    Boolean(
                      pendingCorrectionData?.find(
                        (item) => item.error_type_label === 'Major'
                      )
                    ) ||
                    overallDataCheckDetails?.every(
                      (item) => item.final_check_done
                    )
                  }
                  onClick={() => handleSave(true)}
                >
                  {DATA_CHECK.FINALIZE_DATA_CHECK}
                </CustomButton>
              </Tooltip>
            ) : null}
          </>
        </span>
      </span>
      <OverallDataCheckDetails
        overallDataCheckDetails={overallDataCheckDetails}
      />
      <AccordionFormWithTable
        formHeading={DATA_CHECK.IDENTIFIED_DATA_CORRECTIONS}
        tableHeading={DATA_CHECK.PENDING_DATA_CORRECTIONS}
        tableName="pending-correction-fields"
        selectedRow={selectedRow}
        defaultValues={{
          field_name: '',
          error_type: '',
          description: '',
          error_type_label: '',
        }}
        onSubmit={onSubmit}
        columns={columnList}
        data={pendingCorrectionData}
        actions={isViewMode ? [] : actions}
        disabledKey="disabled"
        tableMinHeight={`${pendingTableHeight}rem`}
        addLabel={NAMESPACES.ADD}
        updateLabel={NAMESPACES.UPDATE_BUTTON}
        cancelLabel={NAMESPACES.CANCEL_BUTTON}
        renderFormFields={(methods, row) =>
          isViewMode ? null : (
            <AddDataFields methods={methods} selectedRow={row} />
          )
        }
      />
      <Accordion
        heading={DATA_CHECK.RESOLVED_DATA_CORRECTIONS}
        tabIndex={TAB_INDEX}
        className="bg-none"
        borderColor="secondary-500"
        isDisabled={false}
        isDefaultActive={true}
      >
        <div className="table-wrap flex w-full">
          <PersistTable
            tableName="resolved-correction-fields"
            data={resolvedCorrectionData}
            columns={resolvedList}
            actions={isViewMode ? [] : resolvedActions}
            disabledKey="disabled"
            emptyDataMessage="No record found."
            isPersistColumns={false}
            minHeight={`${resolvedTableHeight}rem`}
            emptyDataMessageHeight="min-h-32 max-h-32"
          />
        </div>
      </Accordion>
      <AddCommentModal
        isOpen={isAddCommentModalOpen}
        selectedRow={commentRowData}
        commentValue={commentValue}
        onCommentChange={setCommentValue}
        onClose={closeAddCommentModal}
        onSave={handleSaveComment}
      />
    </div>
  )
}

interface OverallDataCheckDetailsProps {
  overallDataCheckDetails: DataType[]
}

export const OverallDataCheckDetails = ({
  overallDataCheckDetails,
}: OverallDataCheckDetailsProps) => {
  const [tableHeight, setTableHeight] = useState<number>(10)

  const dataCheckColumns: TableColumnType<DataTypeStore>[] = [
    {
      header: DATA_CHECK.CYCLE_NO,
      accessor: DATA_CHECK.CYCLE_NUMBER_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.CYCLE_START_DATE,
      accessor: DATA_CHECK.CYCLE_START_DATE_LOWERCASE,
      isSortable: false,
    },
    {
      header: DATA_CHECK.CYCLE_END_DATE,
      accessor: DATA_CHECK.CYCLE_END_DATE_LOWERCASE,
      isSortable: false,
    },
  ]

  useEffect(() => {
    setTableHeight(overallDataCheckDetails?.length >= 3 ? 15 : 10)
  }, [overallDataCheckDetails])

  return (
    <Accordion
      heading={DATA_CHECK.OVERALL_DATA_CHECK_DETAILS}
      tabIndex={TAB_INDEX}
      className="bg-none"
      borderColor="secondary-500"
      isDisabled={false}
      isDefaultActive={true}
    >
      <div className="table-wrap flex w-full">
        <PersistTable
          tableName="resolved-correction-fields"
          data={overallDataCheckDetails}
          columns={dataCheckColumns}
          disabledKey="disabled"
          emptyDataMessage="No record found."
          isPersistColumns={false}
          minHeight={`${tableHeight}rem`}
          emptyDataMessageHeight="min-h-32 max-h-32"
        />
      </div>
    </Accordion>
  )
}
